import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components';
import { useTypingIndicator } from '../../../lib/websocket';
import { useConversationStore, useUIStore } from '../../../stores';
import { debounce } from '../../../lib/utils';
import { uploadFile, UploadedMedia } from '../../../lib/api';

interface MessageInputProps {
  onSend: (message: string, media?: UploadedMedia) => void;
  isSending?: boolean;
}

export default function MessageInput({ onSend, isSending = false }: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { selectedConversationId } = useConversationStore();
  const { composerDraft, setComposerDraft } = useUIStore();
  const { startTyping, stopTyping } = useTypingIndicator(selectedConversationId);

  // "Use to customer" on a copilot reply drops its text here. Load it into the
  // composer (focused, cursor at end) so staff can edit before sending, then
  // clear the draft so it doesn't re-apply.
  useEffect(() => {
    if (composerDraft == null) return;
    setMessage(composerDraft);
    setComposerDraft(null);
    const el = textareaRef.current;
    if (el) {
      el.focus();
      el.value = composerDraft;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
      el.setSelectionRange(composerDraft.length, composerDraft.length);
    }
  }, [composerDraft, setComposerDraft]);

  const debouncedStopTyping = debounce(() => {
    stopTyping();
  }, 1000);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    startTyping();
    debouncedStopTyping();
  };

  const handleSubmit = async () => {
    const text = message.trim();
    if ((!text && !file) || isSending || uploading) return;

    let media: UploadedMedia | undefined;
    if (file) {
      setUploading(true);
      setUploadError(null);
      try {
        media = await uploadFile(file);
      } catch (err: any) {
        setUploadError(err?.response?.data?.detail || 'Upload failed. Please try again.');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    onSend(text, media);
    setMessage('');
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    stopTyping();

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] || null;
    setFile(selected);
    setUploadError(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter inserts a new line (default textarea behavior). Send with the
    // button or ⌘/Ctrl+Enter.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="border-t border-line bg-surface p-3 sm:p-4">
      {file && (
        <div className="mb-2 flex items-center gap-2 rounded-card border border-line bg-surface-2 px-3 py-2 text-sm text-ink-2">
          <PaperClipIcon className="h-4 w-4 shrink-0 text-ink-3" />
          <span className="truncate">{file.name}</span>
          <span className="shrink-0 text-xs text-ink-3">
            {(file.size / 1024).toFixed(0)} KB
          </span>
          <button
            type="button"
            onClick={() => {
              setFile(null);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
            className="ml-auto shrink-0 rounded p-0.5 text-ink-3 hover:bg-black/[0.04] hover:text-ink"
            aria-label="Remove attachment"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}
      {uploadError && <p className="mb-2 text-xs text-danger">{uploadError}</p>}
      <div className="flex items-end gap-2 sm:gap-3">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSending || uploading}
          title="Attach a file"
          aria-label="Attach a file"
          className="mb-0.5 rounded-xl border border-line bg-surface p-3 text-ink-2 hover:bg-black/[0.04] hover:text-ink disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PaperClipIcon className="h-4 w-4" />
        </button>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          title="Enter for a new line · ⌘/Ctrl+Enter to send"
          rows={1}
          className="block w-full resize-none rounded-xl border border-line bg-surface px-4 py-3 text-sm text-ink placeholder:text-ink-3 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          style={{ maxHeight: '120px' }}
        />
        <Button
          onClick={handleSubmit}
          disabled={(!message.trim() && !file) || isSending || uploading}
          isLoading={isSending || uploading}
          rightIcon={<PaperAirplaneIcon className="h-4 w-4" />}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
